import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/Dialog";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import { toast } from "@/hooks/use-toast";

/**
 * Fluxo de exclusão de conta (Priority 15). Dois passos:
 * 1) confirmação explícita ("essa ação é permanente")
 * 2) se o Firebase exigir reautenticação recente (auth/requires-recent-login),
 *    pede senha (conta e-mail/senha) ou reabre o popup do Google — sem isso
 *    a exclusão simplesmente falharia silenciosamente.
 */
export function DeleteAccountDialog() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"confirm" | "reauth">("confirm");
  const [password, setPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const signInMethod = AuthenticationService.getCurrentSignInMethod();

  function reset() {
    setStep("confirm");
    setPassword("");
    setIsDeleting(false);
  }

  async function handleDelete(withPassword?: string) {
    setIsDeleting(true);
    try {
      await AuthenticationService.deleteAccount(withPassword);
      toast({ title: "Conta excluída", description: "Sentiremos sua falta." });
      setOpen(false);
      // A sessão já foi encerrada pelo próprio Firebase ao excluir o usuário;
      // o AuthContext detecta via onAuthStateChanged e redireciona pro login.
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/requires-recent-login" && step === "confirm") {
        setStep("reauth");
        setIsDeleting(false);
        return;
      }
      toast({
        title: "Não foi possível excluir sua conta",
        description: code === "auth/wrong-password" ? "Senha incorreta." : "Tente novamente em instantes.",
        variant: "destructive",
      });
      setIsDeleting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full text-destructive hover:bg-destructive/10">
          <Trash2 className="h-4 w-4" /> Excluir conta
        </Button>
      </DialogTrigger>
      <DialogContent>
        {step === "confirm" ? (
          <>
            <DialogHeader>
              <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <DialogTitle>Excluir conta?</DialogTitle>
              <DialogDescription>
                Essa ação é permanente e não poderá ser desfeita. Seus dados pessoais serão removidos e você será
                desconectado imediatamente.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={isDeleting}>
                Cancelar
              </Button>
              <Button
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                isLoading={isDeleting}
                onClick={() => handleDelete()}
              >
                Excluir definitivamente
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirme sua identidade</DialogTitle>
              <DialogDescription>
                {signInMethod === "google.com"
                  ? "Por segurança, o Firebase pede uma confirmação recente. Vamos reabrir o login do Google."
                  : "Por segurança, digite sua senha atual para confirmar a exclusão."}
              </DialogDescription>
            </DialogHeader>

            {signInMethod === "password" && (
              <div className="space-y-1.5">
                <Label htmlFor="delete-password">Senha</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={isDeleting}>
                Cancelar
              </Button>
              <Button
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                isLoading={isDeleting}
                disabled={signInMethod === "password" && password.trim().length === 0}
                onClick={() => handleDelete(password)}
              >
                Confirmar e excluir
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
